import React from 'react';
import { Box, Grid } from '@mui/material';
import { useHistory, useRouteMatch } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import StatCard from '../../components/StatCard';
import SectionCard from '../../components/SectionCard';
import WorkflowPipeline from '../../components/WorkflowPipeline';
import QuickActionCard from '../../components/QuickActionCard';
import useOverviewStats from '../../hooks/useOverviewStats';
import useInvRole from '../../hooks/useInvRole';
import { INV_ICONS } from '../../constants/icons';
import { getItems } from '../../api/inventoryMaster';
import { getVendors } from '../../api/vendor';
import { getRfqs } from '../../api/rfq';
import { getWorkOrders } from '../../api/workOrder';
import { fromApiWorkOrder, visibleWorkOrderListRows } from '../../utils/woEntity';
import { getPurchaseOrders } from '../../api/procurement';
import { getGrns } from '../../api/grn';
import { getQcRejections } from '../../api/qcRejections';
import { getAwaitingReplacementVendorReturns } from '../../api/vendorReturn';
import { getPurchaseInvoices } from '../../api/purchaseInvoice';
import { INV_ACTIONS } from '../../utils/inventoryRoles';
import { INV_SPACING } from '../../theme';

export default function MaterialOverview() {
  const { url } = useRouteMatch();
  const history = useHistory();
  const base = url.replace(/\/$/, '');
  // Settled by the time this renders — InvRoleRoute holds the spinner until then.
  const { can } = useInvRole();

  const { stats, loading } = useOverviewStats({
    items: () => getItems(),
    vendors: () => getVendors(),
    rfqs: () => getRfqs({ status: 'PUBLISHED' }),
    workOrders: async () => {
      const data = await getWorkOrders();
      const list = Array.isArray(data) ? data : (data?.items || data?.data || []);
      return visibleWorkOrderListRows(list.map(fromApiWorkOrder));
    },
    pos: () => getPurchaseOrders(),
    grns: () => getGrns({ status: 'QC_PENDING' }),
    rejected: () => getQcRejections({ actionable_only: true }),
    awaitingReplacement: () => getAwaitingReplacementVendorReturns(),
    invoices: () => getPurchaseInvoices(),
  });

  // `action` is the permission guarding the route the entry points at — the same
  // convention as NAV_ITEM_ACTIONS in navigation.js, so a tile is shown exactly when
  // the screen behind it would open. Tiles are dropped and pipeline stages dimmed;
  // both counts are permission-dependent, so a partial grid row is expected. A Store
  // Keeper holds only `grn *` and `rejected view` here, so most of this page is gone
  // for them.
  const STAT_CARDS = [
    { key: 'items', label: 'Total Items', icon: INV_ICONS.items, tone: 'teal', path: `${base}/items`, action: INV_ACTIONS.ITEM_MANAGE },
    { key: 'vendors', label: 'Vendors', icon: INV_ICONS.vendors, tone: 'mint', path: `${base}/vendors`, action: INV_ACTIONS.VENDOR_VIEW },
    { key: 'rfqs', label: 'Open RFQs', icon: INV_ICONS.rfqs, tone: 'mint', path: `${base}/rfqs`, action: INV_ACTIONS.RFQ_VIEW },
    { key: 'workOrders', label: 'Work Orders', icon: INV_ICONS.workOrders, tone: 'teal', path: `${base}/work-orders`, action: INV_ACTIONS.RFQ_VIEW },
    { key: 'pos', label: 'Purchase Orders', icon: INV_ICONS.purchaseOrders, tone: 'teal', path: `${base}/pos`, action: INV_ACTIONS.PO_VIEW },
    { key: 'grns', label: 'GRNs Pending QC', icon: INV_ICONS.grns, tone: 'amber', path: `${base}/grns?status=QC_PENDING`, action: INV_ACTIONS.GRN_VIEW },
    { key: 'rejected', label: 'Rejected Items', icon: INV_ICONS.rejectedItems, tone: 'red', path: `${base}/rejected-items`, action: INV_ACTIONS.REJECTED_VIEW },
    { key: 'awaitingReplacement', label: 'Awaiting Replacement', icon: INV_ICONS.vendorReturns, tone: 'amber', path: `${base}/vendor-returns/awaiting-replacement`, action: INV_ACTIONS.VENDOR_RETURN_VIEW },
    { key: 'invoices', label: 'Purchase Invoices', icon: INV_ICONS.purchaseInvoices, tone: 'mint', path: `${base}/purchase-invoices`, action: INV_ACTIONS.PURCHASE_INVOICE_VIEW },
  ].filter(card => can(card.action));

  const QUICK_ACTIONS = [
    { title: 'New Item', description: 'Create an inventory item master record', icon: INV_ICONS.items, path: `${base}/items/new`, action: INV_ACTIONS.ITEM_MANAGE },
    { title: 'New RFQ', description: 'Request quotations from vendors', icon: INV_ICONS.rfqs, path: `${base}/rfq/new`, action: INV_ACTIONS.RFQ_CREATE },
    { title: 'New Work Order', description: 'Direct job or invite vendors to quote', icon: INV_ICONS.workOrders, path: `${base}/work-order/new`, action: INV_ACTIONS.RFQ_CREATE },
    { title: 'New PO', description: 'Raise a purchase order for procurement', icon: INV_ICONS.purchaseOrders, path: `${base}/po/new`, action: INV_ACTIONS.PO_CREATE },
    { title: 'New GRN', description: 'Record goods received against a purchase order', icon: INV_ICONS.grns, path: `${base}/grn/new`, action: INV_ACTIONS.GRN_CREATE },
  ].filter(item => can(item.action));

  const PIPELINE = [
    { label: 'Material Groups', path: `${base}/material-groups`, action: INV_ACTIONS.MASTER_MANAGE },
    { label: 'Item Classes', path: `${base}/item-classes`, action: INV_ACTIONS.MASTER_MANAGE },
    { label: 'Categories', path: `${base}/categories`, action: INV_ACTIONS.MASTER_MANAGE },
    { label: 'UOMs', path: `${base}/uoms`, action: INV_ACTIONS.MASTER_MANAGE },
    { label: 'Items', path: `${base}/items`, action: INV_ACTIONS.ITEM_MANAGE },
    { label: 'Vendors', path: `${base}/vendors`, action: INV_ACTIONS.VENDOR_VIEW },
    { label: 'RFQ', path: `${base}/rfqs`, action: INV_ACTIONS.RFQ_VIEW },
    { label: 'Work Order', path: `${base}/work-orders`, action: INV_ACTIONS.RFQ_VIEW },
    { label: 'PO', path: `${base}/pos`, action: INV_ACTIONS.PO_VIEW },
    { label: 'GRN', path: `${base}/grns`, action: INV_ACTIONS.GRN_VIEW },
  ].map(stage => ({ ...stage, disabled: !can(stage.action) }));

  return (
    <Box sx={{ maxWidth: '100%' }}>
      <PageHeader
        breadcrumb={['Inventory Suite', 'Material Management']}
        title="Overview"
        description="Item masters, vendors and procurement at a glance"
      />

      {STAT_CARDS.length > 0 && (
        <Grid container spacing={INV_SPACING.grid} sx={{ mb: INV_SPACING.section }}>
          {STAT_CARDS.map((card) => (
            <Grid item xs={6} md={3} key={card.key}>
              <StatCard
                label={card.label}
                value={stats[card.key]}
                icon={card.icon}
                tone={card.tone}
                loading={loading}
                onClick={() => history.push(card.path)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <SectionCard title="Procurement Workflow" sx={{ mb: INV_SPACING.section }}>
        <WorkflowPipeline stages={PIPELINE} />
      </SectionCard>

      {QUICK_ACTIONS.length > 0 && (
        <SectionCard title="Quick Actions">
          <Grid container spacing={INV_SPACING.grid}>
            {QUICK_ACTIONS.map((action) => (
              <QuickActionCard
                key={action.title}
                icon={action.icon}
                title={action.title}
                description={action.description}
                onClick={() => history.push(action.path)}
                xs={12}
                sm={6}
                md={3}
                lg={3}
              />
            ))}
          </Grid>
        </SectionCard>
      )}
    </Box>
  );
}
